import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const SectionContainer = styled(Box)({
  padding: '120px 60px',
  background: '#faf9f7',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '500px',
    height: '500px',
    background: 'radial-gradient(circle, rgba(201, 162, 39, 0.06) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  position: 'relative',
  zIndex: 10
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '64px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(201, 162, 39, 0.1)',
  border: '1px solid rgba(201, 162, 39, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#c9a227',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 400,
  color: '#1a1a1a',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #c9a227 0%, #a67c52 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const MainGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '48px',
  alignItems: 'stretch',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: '1fr',
    gap: '40px'
  }
});

const ContactCard = styled(Box)({
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  borderRadius: '28px',
  padding: '48px',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: '0.1s',
  animationFillMode: 'backwards',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #d4af37, #f4e4bc)'
  },
  '@media (max-width: 768px)': {
    padding: '36px'
  }
});

const CardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '16px'
});

const IconBox = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 8px 25px rgba(212, 175, 55, 0.3)',
  '& svg': {
    fontSize: '26px',
    color: '#1a1a2e'
  }
});

const CardTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: '32px',
  fontWeight: 400,
  color: '#ffffff',
  letterSpacing: '-0.5px'
});

const CardSubtitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.5)',
  marginBottom: '36px'
});

const ContactList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginBottom: '36px'
});

const ContactItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '20px 24px',
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.06)',
    borderColor: 'rgba(212, 175, 55, 0.3)',
    transform: 'translateX(4px)',
    '& .item-icon': {
      background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
      '& svg': {
        color: '#1a1a2e'
      }
    },
    '& .item-arrow': {
      opacity: 1,
      transform: 'translateX(0)',
      color: '#d4af37'
    }
  }
});

const ItemIcon = styled(Box)({
  width: '44px',
  height: '44px',
  borderRadius: '12px',
  background: 'rgba(212, 175, 55, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  '& svg': {
    fontSize: '20px',
    color: '#d4af37',
    transition: 'all 0.3s ease'
  }
});

const ItemContent = styled(Box)({
  flex: 1
});

const ItemLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '11px',
  fontWeight: 600,
  color: '#d4af37',
  textTransform: 'uppercase',
  letterSpacing: '1.5px',
  marginBottom: '4px'
});

const ItemValue = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 500,
  color: '#ffffff'
});

const ArrowIcon = styled(ArrowForwardIcon)({
  fontSize: '18px',
  color: 'rgba(255, 255, 255, 0.4)',
  opacity: 0,
  transform: 'translateX(-8px)',
  transition: 'all 0.3s ease'
});

const DirectionsButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '18px 36px',
  background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
  borderRadius: '50px',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 8px 30px rgba(212, 175, 55, 0.3)',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 40px rgba(212, 175, 55, 0.4)'
  }
});

const ButtonText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 600,
  color: '#1a1a2e'
});

const MapWrapper = styled(Box)({
  borderRadius: '28px',
  overflow: 'hidden',
  height: '100%',
  minHeight: '500px',
  position: 'relative',
  animation: `${fadeInRight} 0.8s ease-out`,
  animationDelay: '0.2s',
  animationFillMode: 'backwards',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  '@media (max-width: 1024px)': {
    minHeight: '400px'
  }
});

const MapIframe = styled('iframe')({
  width: '100%',
  height: '100%',
  border: 'none',
  position: 'absolute',
  top: 0,
  left: 0
});

const MapOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.05) 0%, rgba(15, 15, 15, 0.1) 100%)',
  pointerEvents: 'none',
  borderRadius: '28px'
});

const LocationSection = ({ location, contactInfo, businessInfo }) => {
  const lat = location?.lat ?? null;
  const lng = location?.lng ?? null;
  const address = contactInfo?.address || location?.address;
  const businessName = businessInfo?.name || location?.businessName || '';
  const hasCoordinates = lat !== null && lng !== null;
  // Use business name + address for exact pin placement
  const mapQuery = (businessName && address) ? `${businessName} ${address}` : (address || (hasCoordinates ? `${lat},${lng}` : ''));

  const handlePhoneClick = () => {
    if (contactInfo?.phone) window.location.href = `tel:${contactInfo.phone}`;
  };

  const handleEmailClick = () => {
    if (contactInfo?.email) window.location.href = `mailto:${contactInfo.email}`;
  };

  const handleDirectionsClick = () => {
    if (address) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`, '_blank');
    }
  };

  const mapUrl = mapQuery ? `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(mapQuery)}&zoom=15` : null;

  return (
    <SectionContainer id="contact">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <BadgeText>Contact Us</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Get in <GradientText>Touch</GradientText>
          </SectionTitle>
        </SectionHeader>

        <MainGrid>
          <ContactCard>
            <CardHeader>
              <IconBox>
                <LocationOnIcon />
              </IconBox>
              <CardTitle>Contact Info</CardTitle>
            </CardHeader>

            <CardSubtitle>We'd love to hear from you</CardSubtitle>

            <ContactList>
              {address && (
                <ContactItem onClick={handleDirectionsClick}>
                  <ItemIcon className="item-icon">
                    <LocationOnIcon />
                  </ItemIcon>
                  <ItemContent>
                    <ItemLabel>Address</ItemLabel>
                    <ItemValue>{address}</ItemValue>
                  </ItemContent>
                  <ArrowIcon className="item-arrow" />
                </ContactItem>
              )}

              {contactInfo?.phone && (
                <ContactItem onClick={handlePhoneClick}>
                  <ItemIcon className="item-icon">
                    <PhoneIcon />
                  </ItemIcon>
                  <ItemContent>
                    <ItemLabel>Phone</ItemLabel>
                    <ItemValue>{contactInfo.phone}</ItemValue>
                  </ItemContent>
                  <ArrowIcon className="item-arrow" />
                </ContactItem>
              )}

              {contactInfo?.email && (
                <ContactItem onClick={handleEmailClick}>
                  <ItemIcon className="item-icon">
                    <EmailIcon />
                  </ItemIcon>
                  <ItemContent>
                    <ItemLabel>Email</ItemLabel>
                    <ItemValue>{contactInfo.email}</ItemValue>
                  </ItemContent>
                  <ArrowIcon className="item-arrow" />
                </ContactItem>
              )}

              {contactInfo?.website && (
                <ContactItem onClick={() => window.open(contactInfo.website, '_blank')}>
                  <ItemIcon className="item-icon">
                    <LanguageIcon />
                  </ItemIcon>
                  <ItemContent>
                    <ItemLabel>Website</ItemLabel>
                    <ItemValue>{contactInfo.website}</ItemValue>
                  </ItemContent>
                  <ArrowIcon className="item-arrow" />
                </ContactItem>
              )}
            </ContactList>

            <DirectionsButton onClick={handleDirectionsClick}>
              <ButtonText>Get Directions</ButtonText>
              <ArrowForwardIcon sx={{ fontSize: 20, color: '#1a1a2e' }} />
            </DirectionsButton>
          </ContactCard>

          <MapWrapper>
            <MapIframe
              src={mapUrl}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Business Location"
            />
            <MapOverlay />
          </MapWrapper>
        </MainGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default LocationSection;
